
export const generateInstallationDocs = (): string => {
  return `# 7GC Dashboard - Kompletna instalacijska dokumentacija
=================================================

## Sadržaj
1. Preduvjeti
2. Skidanje projekta
3. Instalacija ovisnosti
4. Konfiguracija
5. Build procesa
6. Postavljanje web servera
7. SSL konfiguracija
8. Rješavanje čestih problema

## 1. Preduvjeti

Sljedeći softver mora biti instaliran na serveru:
- Node.js (verzija 18.x ili novija)
- npm (obično dolazi s Node.js)
- Nginx ili Apache web server
- Git (opcionalno za preuzimanje projekta)

Za Ubuntu/Debian:
\`\`\`
sudo apt update
sudo apt install -y curl wget git
curl -fsSL https://deb.nodesource.com/setup_18.x | sudo -E bash -
sudo apt install -y nodejs nginx
\`\`\`

## 2. Skidanje projekta

### Opcija A: Koristeći Git
\`\`\`
git clone <url-projekta> 7gc-dashboard
cd 7gc-dashboard
\`\`\`

### Opcija B: Ručni upload
1. Prenesite ZIP arhivu projekta na server
2. Raspakirajte u direktorij:
\`\`\`
mkdir 7gc-dashboard
unzip projekt.zip -d 7gc-dashboard
cd 7gc-dashboard
\`\`\`

## 3. Instalacija ovisnosti

Pokrenite sljedeće naredbe:
\`\`\`
npm install
\`\`\`

### Popis potrebnih paketa:
- @hookform/resolvers: ^3.9.0
- @radix-ui/react-* (više komponenti)
- @react-three/drei: ^9.99.7
- @tanstack/react-query: ^5.56.2
- class-variance-authority: ^0.7.1
- clsx: ^2.1.1
- cmdk: ^1.0.0
- date-fns: ^3.6.0
- embla-carousel-react: ^8.3.0
- input-otp: ^1.2.4
- lucide-react: ^0.462.0
- next-themes: ^0.3.0
- react: ^18.3.1
- react-dom: ^18.3.1
- react-hook-form: ^7.53.0
- react-resizable-panels: ^2.1.3
- react-router-dom: ^6.26.2
- recharts: ^2.12.7
- sonner: ^1.5.0
- tailwind-merge: ^2.5.2
- tailwindcss-animate: ^1.0.7
- three: ^0.157.0
- vaul: ^0.9.3
- zod: ^3.23.8

## 4. Konfiguracija

Prije kompilacije, provjerite sljedeće datoteke:

1. \`vite.config.ts\` - Prilagodite port i host ako je potrebno
2. \`.env\` datoteka za eventualne environment varijable (ako postoje)

## 5. Build procesa

Za produkcijski build:
\`\`\`
npm run build
\`\`\`

Nakon uspješnog builda, \`dist\` direktorij sadrži sve datoteke koje treba postaviti na web server.

## 6. Postavljanje web servera

### Za Nginx:

1. Kopirajte \`dist\` folder u web root direktorij:
\`\`\`
sudo mkdir -p /var/www/7gc-dashboard
sudo cp -r dist/* /var/www/7gc-dashboard/
sudo chown -R www-data:www-data /var/www/7gc-dashboard
\`\`\`

2. Kreirajte Nginx konfiguracijsku datoteku:
\`\`\`
sudo nano /etc/nginx/sites-available/7gc-dashboard
\`\`\`

3. Dodajte sljedeću konfiguraciju:
\`\`\`
server {
    listen 80;
    server_name vaš-domena.com;
    
    root /var/www/7gc-dashboard;
    index index.html;
    
    location / {
        try_files $uri $uri/ /index.html;
    }
    
    # Dodajte cache pravila za statične assete
    location ~* \\.(?:jpg|jpeg|gif|png|ico|svg|js|css)$ {
        expires 30d;
        add_header Cache-Control "public, no-transform";
    }
    
    # Logging
    access_log /var/log/nginx/7gc-dashboard-access.log;
    error_log /var/log/nginx/7gc-dashboard-error.log;
}
\`\`\`

4. Aktivirajte konfiguraciju:
\`\`\`
sudo ln -s /etc/nginx/sites-available/7gc-dashboard /etc/nginx/sites-enabled/
sudo nginx -t
sudo systemctl restart nginx
\`\`\`

### Za Apache:

1. Kopirajte build u web direktorij:
\`\`\`
sudo mkdir -p /var/www/7gc-dashboard
sudo cp -r dist/* /var/www/7gc-dashboard/
sudo chown -R www-data:www-data /var/www/7gc-dashboard
\`\`\`

2. Kreirajte Apache virtualhost:
\`\`\`
sudo nano /etc/apache2/sites-available/7gc-dashboard.conf
\`\`\`

3. Dodajte konfiguraciju:
\`\`\`
<VirtualHost *:80>
    ServerName vaš-domena.com
    DocumentRoot /var/www/7gc-dashboard
    
    <Directory /var/www/7gc-dashboard>
        Options -Indexes +FollowSymLinks
        AllowOverride All
        Require all granted
    </Directory>
    
    ErrorLog \${APACHE_LOG_DIR}/7gc-dashboard-error.log
    CustomLog \${APACHE_LOG_DIR}/7gc-dashboard-access.log combined
    
    <IfModule mod_rewrite.c>
        RewriteEngine On
        RewriteBase /
        RewriteRule ^index\\.html$ - [L]
        RewriteCond %{REQUEST_FILENAME} !-f
        RewriteCond %{REQUEST_FILENAME} !-d
        RewriteRule . /index.html [L]
    </IfModule>
</VirtualHost>
\`\`\`

4. Aktivirajte konfiguraciju:
\`\`\`
sudo a2ensite 7gc-dashboard.conf
sudo a2enmod rewrite
sudo systemctl restart apache2
\`\`\`

## 7. SSL konfiguracija

Za HTTPS, preporučujemo Let's Encrypt certifikat.

### Za Nginx:
\`\`\`
sudo apt install -y certbot python3-certbot-nginx
sudo certbot --nginx -d vaš-domena.com
\`\`\`

### Za Apache:
\`\`\`
sudo apt install -y certbot python3-certbot-apache
sudo certbot --apache -d vaš-domena.com
\`\`\`

## 8. Rješavanje čestih problema

### Problem: Stranica se ne učitava
- Provjerite je li Nginx/Apache pokrenut: \`sudo systemctl status nginx\`
- Provjerite logove: \`sudo tail -f /var/log/nginx/7gc-dashboard-error.log\`
- Provjerite jesu li datoteke ispravno kopirane u web direktorij

### Problem: API zahtjevi ne rade
- Konfigurirajte CORS na backendima ako postoje
- Provjerite jesu li API endpoint-ovi ispravno konfigurirani u aplikaciji

### Problem: CSS ili JavaScript ne radi
- Provjerite putanje u index.html
- Provjerite browser konzolu za errors

### Problem: Bijeli ekran (blank page)
- Provjerite konzolu u dev alatima preglednika
- Provjerite ispravnost build procesa

## Automatizirana instalacija

Za potpuno automatiziranu instalaciju, spremite ovaj skript kao \`install.sh\`:

\`\`\`bash
#!/bin/bash

# 7GC Dashboard Installer
# -----------------------

echo "=== 7GC Dashboard Installer ==="
echo "Ovaj skript će instalirati sve potrebno za 7GC Dashboard"

# Provjera root privilegija
if [ "$EUID" -ne 0 ]; then
  echo "Molimo pokrenite skript kao root korisnik (sudo)"
  exit 1
fi

# Ažuriranje sustava
echo "Ažuriranje sustava..."
apt update
apt upgrade -y

# Instalacija neophodnih paketa
echo "Instalacija potrebnih paketa..."
apt install -y curl wget git nginx certbot python3-certbot-nginx

# Instalacija Node.js
echo "Instalacija Node.js..."
curl -fsSL https://deb.nodesource.com/setup_18.x | bash -
apt install -y nodejs

# Provjera verzija
echo "Node.js verzija: $(node -v)"
echo "NPM verzija: $(npm -v)"

# Priprema direktorija
echo "Priprema web direktorija..."
mkdir -p /var/www/7gc-dashboard

# Pitaj za izvor instalacije
read -p "Postaviti aplikaciju iz lokalnog direktorija? (y/n): " local_setup

if [ "$local_setup" = "y" ]; then
  read -p "Unesi putanju do build direktorija: " build_dir
  
  if [ ! -d "$build_dir" ]; then
    echo "Direktorij ne postoji!"
    exit 1
  fi
  
  echo "Kopiranje datoteka iz $build_dir u web direktorij..."
  cp -r "$build_dir"/* /var/www/7gc-dashboard/
else
  # Ovdje možemo dodati opciju za git clone ili drugi izvor
  echo "Molimo kopirajte datoteke ručno u /var/www/7gc-dashboard/"
  exit 0
fi

# Postavljanje vlasnika
echo "Postavljanje dozvola..."
chown -R www-data:www-data /var/www/7gc-dashboard
chmod -R 755 /var/www/7gc-dashboard

# Konfiguracija Nginx
echo "Konfiguriranje Nginx..."
cat > /etc/nginx/sites-available/7gc-dashboard <<EOF
server {
    listen 80;
    server_name _;
    
    root /var/www/7gc-dashboard;
    index index.html;
    
    location / {
        try_files \$uri \$uri/ /index.html;
    }
    
    # Dodajte cache pravila za statične assete
    location ~* \\.(?:jpg|jpeg|gif|png|ico|svg|js|css)$ {
        expires 30d;
        add_header Cache-Control "public, no-transform";
    }
    
    # Logging
    access_log /var/log/nginx/7gc-dashboard-access.log;
    error_log /var/log/nginx/7gc-dashboard-error.log;
}
EOF

# Aktivacija konfiguracije
ln -sf /etc/nginx/sites-available/7gc-dashboard /etc/nginx/sites-enabled/
nginx -t

if [ $? -ne 0 ]; then
  echo "Greška u Nginx konfiguraciji!"
  exit 1
fi

# Restart Nginx
systemctl restart nginx

# Pitanje za SSL
read -p "Konfigurirati SSL s Let's Encrypt? (y/n): " setup_ssl

if [ "$setup_ssl" = "y" ]; then
  read -p "Unesi domenu (npr. domena.com): " domain_name
  
  if [ -z "$domain_name" ]; then
    echo "Domena nije unesena!"
    exit 1
  fi
  
  # Ažurirati Nginx konfiguraciju s domenom
  sed -i "s/server_name _;/server_name $domain_name;/" /etc/nginx/sites-available/7gc-dashboard
  
  # Instalacija SSL certifikata
  certbot --nginx -d $domain_name
fi

echo "====================================="
echo "7GC Dashboard instalacija završena!"
echo "Možete pristupiti aplikaciji na:"

if [ "$setup_ssl" = "y" ]; then
  echo "https://$domain_name"
else
  echo "http://$(hostname -I | cut -d' ' -f1)"
fi

echo "====================================="
echo "Za pristup logovima: tail -f /var/log/nginx/7gc-dashboard-error.log"
\`\`\`

-----

Za probleme ili dodatnu pomoć, kontaktirajte administratora.`;
};
